/**
 * MfaEnrollmentBanner — banner persistente que aparece quando o cargo do
 * usuario exige MFA mas ele ainda nao enrollou (cenario 3 do Sprint 10).
 *
 * Mecanica:
 *  - `AuthContext` expoe `mfaEnrollmentRequired` (booleano).
 *  - O banner se auto-renderiza em qualquer pagina autenticada.
 *  - Clicar "Configurar agora" abre o `MfaEnrollmentPanel` ja existente.
 *  - Apos enrollment concluido, `dismissMfaEnrollmentRequired()` limpa a
 *    flag e o banner some.
 *
 * Decisao consciente: NAO bloqueia navegacao (apenas chama atencao). Bloqueio
 * de rotas sensiveis pode ser adicionado em sprint futuro se a auditoria
 * pedir.
 */
import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiAlertTriangle, FiShield } from "react-icons/fi";

import MfaEnrollmentPanel from "../MfaEnrollmentPanel";
import { useAuth } from "../../context/AuthContext";

const BannerWrap = styled.div`
  /* P2: faixa de largura total no topo (cobre tambem a coluna da sidebar),
     logo abaixo do header. Fica fixa; a sidebar e o conteudo descem por
     --mfa-banner-h (setada no efeito abaixo) para nada ficar atras. */
  position: fixed;
  top: calc(var(--topbar-height) + var(--safe-area-top, 0px));
  left: 0;
  right: 0;
  z-index: 95;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  background: ${({ theme }) => theme.warningSurface};
  color: ${({ theme }) => theme.warningText};
  border-bottom: 1px solid ${({ theme }) => theme.warningBorder};
  font-size: 13px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
`;

const BannerText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  line-height: 1.4;
`;

const BannerButton = styled.button`
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }
`;

export default function MfaEnrollmentBanner() {
  const { mfaEnrollmentRequired, dismissMfaEnrollmentRequired } = useAuth();
  const [aberto, setAberto] = useState(false);
  const bannerRef = useRef(null);

  // Exporta a altura real da faixa em --mfa-banner-h para a sidebar/conteudo
  // descerem (a faixa eh `position: fixed`). Remove a var quando some.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!mfaEnrollmentRequired) {
      root.style.removeProperty("--mfa-banner-h");
      return undefined;
    }
    const el = bannerRef.current;
    if (!el) return undefined;
    const aplicar = () =>
      root.style.setProperty("--mfa-banner-h", `${el.offsetHeight}px`);
    aplicar();
    const ro = new ResizeObserver(aplicar);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--mfa-banner-h");
    };
  }, [mfaEnrollmentRequired]);

  if (!mfaEnrollmentRequired) return null;

  return (
    <>
      <BannerWrap ref={bannerRef} role="alert">
        <BannerText>
          <FiAlertTriangle size={16} aria-hidden />
          Seu cargo exige autenticação em duas etapas. Configure agora para
          continuar usando o sistema com segurança.
        </BannerText>
        <BannerButton type="button" onClick={() => setAberto(true)}>
          <FiShield size={14} /> Configurar agora
        </BannerButton>
      </BannerWrap>
      <MfaEnrollmentPanel
        open={aberto}
        onClose={() => setAberto(false)}
        onCompleted={() => {
          dismissMfaEnrollmentRequired();
          setAberto(false);
        }}
      />
    </>
  );
}
